import React, { useEffect, useState } from "react";
import { api, timeAgo, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Wallet as WalletIcon, ArrowDownToLine, TrendingUp, CheckCircle2, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

export default function Wallet() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState({ balance: 0, total_earned: 0, history: [] });
  const [loading, setLoading] = useState(true);
  const [connect, setConnect] = useState({ has_account: false, charges_enabled: false, payouts_enabled: false, details_submitted: false });
  const [connectLoading, setConnectLoading] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [w, c] = await Promise.all([
        api.get("/wallet").then((r) => r.data),
        api.get("/connect/status").then((r) => r.data).catch(() => ({ has_account: false })),
      ]);
      setData(w);
      setConnect(c);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const startOnboarding = async () => {
    setErr(null);
    setConnectLoading(true);
    try {
      const { data } = await api.post("/connect/onboarding", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setConnectLoading(false);
    }
  };

  const cashOut = async () => {
    setErr(null);
    setMsg(null);
    setWithdrawing(true);
    try {
      const { data } = await api.post("/connect/withdraw");
      setMsg(`✅ $${data.amount.toFixed(2)} on its way — Stripe will deposit it in 1–2 business days.`);
      await refresh();
      await loadAll();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setWithdrawing(false);
    }
  };

  const payoutsReady = connect.has_account && connect.payouts_enabled && connect.details_submitted;
  const onboardingStarted = connect.has_account && !payoutsReady;

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Wallet.</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 brut-card p-7 bg-[var(--cs-money)]">
          <div className="text-xs uppercase tracking-widest font-bold">Available balance</div>
          <div className="font-display tabular text-7xl sm:text-8xl font-black leading-none mt-2">
            ${data.balance.toFixed(2)}
          </div>
          <div className="mt-5 flex flex-wrap gap-3 items-center">
            {payoutsReady ? (
              <button
                data-testid="withdraw-btn"
                className="brut-btn brut-btn-ink"
                onClick={cashOut}
                disabled={withdrawing || data.balance < 1}
              >
                {withdrawing ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><ArrowDownToLine size={16} /> Cash out to bank</>}
              </button>
            ) : (
              <button
                data-testid="setup-payouts-btn"
                className="brut-btn brut-btn-ink"
                onClick={startOnboarding}
                disabled={connectLoading}
              >
                {connectLoading ? <><Loader2 size={16} className="animate-spin" /> Opening Stripe…</> : <><ExternalLink size={16} /> {onboardingStarted ? "Finish payouts setup" : "Set up payouts"}</>}
              </button>
            )}
            <div className="brut-chip bg-white">
              <TrendingUp size={12} /> Lifetime ${data.total_earned.toFixed(2)}
            </div>
          </div>
        </div>
        <div className="brut-card p-5">
          <div className="text-xs uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">Payouts</div>
          {!connect.has_account && (
            <div className="mt-2">
              <div className="font-display text-2xl font-black">Not set up</div>
              <p className="text-sm text-[var(--cs-ink-2)] mt-1">Link a bank via Stripe to receive cash.</p>
            </div>
          )}
          {connect.has_account && payoutsReady && (
            <div className="mt-2">
              <div className="font-display text-2xl font-black text-[var(--cs-money-2)] inline-flex items-center gap-1">
                <CheckCircle2 size={20} /> Ready
              </div>
              <p className="text-sm text-[var(--cs-ink-2)] mt-1">ACH deposits to your bank · 1–2 days.</p>
            </div>
          )}
          {connect.has_account && !payoutsReady && (
            <div className="mt-2">
              <div className="font-display text-xl font-black text-[var(--cs-flame)] inline-flex items-center gap-1">
                <AlertTriangle size={18} /> Action needed
              </div>
              <p className="text-sm text-[var(--cs-ink-2)] mt-1">
                Stripe needs a few more details: {connect.details_submitted ? "verifying…" : "ID + bank info"}.
              </p>
            </div>
          )}
        </div>
      </div>

      {err && (
        <div className="brut-card p-4 bg-red-50 text-red-700 font-semibold text-sm" style={{ boxShadow: "3px 3px 0 0 #b91c1c", borderColor: "#b91c1c" }} data-testid="wallet-err">
          {err}
        </div>
      )}
      {msg && (
        <div className="brut-card p-4 bg-[var(--cs-money)] font-semibold text-sm" data-testid="wallet-msg">{msg}</div>
      )}

      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-[var(--cs-ink-2)] mb-3">Transaction history</div>
        {loading ? (
          <div className="text-center py-10 text-[var(--cs-ink-2)] font-bold uppercase tracking-widest text-sm">Loading…</div>
        ) : data.history.length === 0 ? (
          <div className="brut-card p-8 text-center text-[var(--cs-ink-2)]">No transactions yet. Complete a task to earn your first $.</div>
        ) : (
          <div className="brut-card overflow-hidden">
            {data.history.map((row, i) => (
              <div key={row.id} className={`p-4 flex items-center justify-between ${i !== data.history.length - 1 ? "border-b-2" : ""}`} style={{ borderColor: "var(--cs-ink)" }} data-testid={`wallet-row-${i}`}>
                <div>
                  <div className="font-bold">{row.title}</div>
                  <div className="text-xs text-[var(--cs-ink-2)]">{timeAgo(row.date)} · Fee ${row.fee.toFixed(2)}</div>
                </div>
                <div className="font-display tabular text-2xl font-extrabold text-[var(--cs-money-2)]">
                  +${row.amount.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
